import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ServicePageSection, BenefitItem, BusinessType, Sector, StateInfo } from '../../types/servicePages';
import PackagesSection from './PackagesSection';

// Individual Section Components

interface BenefitsSectionProps {
  section: ServicePageSection;
  countryName?: string;
}

const BenefitsSection: React.FC<BenefitsSectionProps> = ({ section, countryName }) => {
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';

  const benefits = section.items?.map(item => {
    // Parse metadata if it's a string
    let metadata = item.metadata;
    if (typeof metadata === 'string') {
      try {
        metadata = JSON.parse(metadata);
      } catch (e) {
        metadata = {};
      }
    }

    return {
      icon: item.icon || '⭐',
      title: item.title || '',
      description: item.description || '',
      successRate: metadata?.successRate || null,
      statistic: metadata?.statistic || null
    };
  }) || [];

  if (benefits.length === 0) return null;

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {section.title || `Why Choose ${countryName || 'This Country'}?`}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {section.subtitle || 'Discover the advantages of setting up your business here.'}
          </p>
        </motion.div>

        {/* Content field display */}
        {section.content && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto mb-12"
          >
            <div className="prose prose-lg max-w-none text-center">
              <div dangerouslySetInnerHTML={{ __html: section.content }} />
            </div>
          </motion.div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="text-center group"
            >
              <div className="text-5xl mb-6 group-hover:scale-110 transition-transform duration-300">
                {benefit.icon}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {benefit.title}
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                {benefit.description}
              </p>
              {(benefit.successRate || benefit.statistic) && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  {benefit.successRate && (
                    <div className="text-sm text-blue-600 font-semibold">
                      ✓ {benefit.successRate} {isArabic ? 'معدل النجاح' : 'Success Rate'}
                    </div>
                  )}
                  {benefit.statistic && (
                    <div className="text-sm text-green-600 font-semibold mt-1">
                      📊 {benefit.statistic}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

interface SectorsSectionProps {
  section: ServicePageSection;
}

const SectorsSection: React.FC<SectorsSectionProps> = ({ section }) => {
  const sectors: Sector[] = section.items?.map(item => {
    // Parse metadata if it's a string
    let metadata = item.metadata;
    if (typeof metadata === 'string') {
      try {
        metadata = JSON.parse(metadata);
      } catch (e) {
        metadata = {};
      }
    }

    return {
      name: item.title || '',
      growth: metadata?.growth || '0%',
      icon: item.icon || '📈'
    };
  }) || [];

  if (sectors.length === 0) return null;

  return (
    <section className="py-16 bg-gradient-to-r from-blue-50 to-red-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {section.title || 'Growing Sectors'}
          </h2>
          <p className="text-xl text-gray-600">
            {section.subtitle || 'Explore thriving business sectors with exceptional growth potential.'}
          </p>
        </motion.div>

        {/* Content field display */}
        {section.content && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto mb-12"
          >
            <div className="prose prose-lg max-w-none text-center">
              <div dangerouslySetInnerHTML={{ __html: section.content }} />
            </div>
          </motion.div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {sectors.map((sector, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 group"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="text-3xl group-hover:scale-110 transition-transform duration-300">
                  {sector.icon}
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-600">{sector.growth}</div>
                  <div className="text-xs text-gray-500">Annual Growth</div>
                </div>
              </div>
              <h3 className="text-lg font-bold text-gray-900">
                {sector.name}
              </h3>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

interface BusinessTypesSectionProps {
  section: ServicePageSection;
}

const BusinessTypesSection: React.FC<BusinessTypesSectionProps> = ({ section }) => {
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';

  const businessTypes: BusinessType[] = section.items?.map(item => {
    // Parse metadata if it's a string
    let metadata = item.metadata;
    if (typeof metadata === 'string') {
      try {
        metadata = JSON.parse(metadata);
      } catch (e) {
        metadata = {};
      }
    }

    return {
      name: item.title || '',
      description: item.description || '',
      minCapital: metadata?.minCapital || metadata?.min_capital || 'N/A',
      foreignOwnership: metadata?.foreignOwnership || metadata?.foreign_ownership || 'N/A',
      taxation: metadata?.taxation || 'N/A',
      liability: metadata?.liability || 'N/A'
    };
  }) || [];

  if (businessTypes.length === 0) return null;

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {section.title || 'Business Types'}
          </h2>
          <p className="text-xl text-gray-600">
            {section.subtitle || 'Choose the right business structure for your needs.'}
          </p>
        </motion.div>

        {/* Content field display */}
        {section.content && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto mb-12"
          >
            <div className="prose prose-lg max-w-none text-center">
              <div dangerouslySetInnerHTML={{ __html: section.content }} />
            </div>
          </motion.div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {businessTypes.map((type, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="bg-gray-50 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {type.name}
              </h3>
              <p className="text-gray-600 mb-4">{type.description}</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-gray-500 block">{isArabic ? 'رأس المال الأدنى' : 'Min Capital'}</span>
                  <span className="text-blue-600 font-semibold">{type.minCapital}</span>
                </div>
                <div>
                  <span className="text-sm text-gray-500 block">{isArabic ? 'الملكية الأجنبية' : 'Foreign Ownership'}</span>
                  <span className="text-green-600 font-semibold">{type.foreignOwnership}</span>
                </div>
                {type.taxation && type.taxation !== 'N/A' && (
                  <div>
                    <span className="text-sm text-gray-500 block">{isArabic ? 'الضرائب' : 'Taxation'}</span>
                    <span className="text-purple-600 font-semibold">{type.taxation}</span>
                  </div>
                )}
                {type.liability && type.liability !== 'N/A' && (
                  <div>
                    <span className="text-sm text-gray-500 block">{isArabic ? 'المسؤولية' : 'Liability'}</span>
                    <span className="text-orange-600 font-semibold">{type.liability}</span>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

interface RequirementsSectionProps {
  section: ServicePageSection;
}

const RequirementsSection: React.FC<RequirementsSectionProps> = ({ section }) => {
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';

  const requirements = section.items?.map(item => {
    // Parse metadata if it's a string
    let metadata = item.metadata;
    if (typeof metadata === 'string') {
      try {
        metadata = JSON.parse(metadata);
      } catch (e) {
        metadata = {};
      }
    }

    return {
      title: item.title || '',
      description: item.description || '',
      processingTime: metadata?.processingTime || null,
      documentCount: metadata?.documentCount || null
    };
  }).filter(req => req.title || req.description) || [];

  if (requirements.length === 0) return null;

  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {section.title || 'Requirements'}
          </h2>
          <p className="text-xl text-gray-600">
            {section.subtitle || 'Everything you need to get started.'}
          </p>
        </motion.div>

        {/* Content field display */}
        {section.content && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto mb-12"
          >
            <div className="prose prose-lg max-w-none text-center">
              <div dangerouslySetInnerHTML={{ __html: section.content }} />
            </div>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="bg-gradient-to-r from-blue-50 to-red-50 rounded-2xl p-8 shadow-lg"
        >
          <ul className="space-y-4">
            {requirements.map((requirement, index) => (
              <motion.li
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex items-start"
              >
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-0.5">
                  <span className="text-blue-600 text-sm font-bold">{index + 1}</span>
                </div>
                <div className="flex-1">
                  <span className="text-gray-700 font-medium">{requirement.title || requirement.description}</span>
                  {requirement.description && requirement.title && (
                    <p className="text-sm text-gray-600 mt-1">{requirement.description}</p>
                  )}
                  {(requirement.processingTime || requirement.documentCount) && (
                    <div className="flex gap-4 mt-2">
                      {requirement.processingTime && (
                        <span className="text-xs text-blue-600 font-semibold">
                          ⏱️ {requirement.processingTime}
                        </span>
                      )}
                      {requirement.documentCount && (
                        <span className="text-xs text-green-600 font-semibold">
                          📄 {requirement.documentCount} {isArabic ? (requirement.documentCount === '1' ? 'وثيقة' : 'وثائق') : (requirement.documentCount === '1' ? 'document' : 'documents')}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </motion.li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
};

interface StatesSectionProps {
  section: ServicePageSection;
}

const StatesSection: React.FC<StatesSectionProps> = ({ section }) => {
  const states: StateInfo[] = section.items?.map(item => ({
    name: item.title || '',
    benefits: item.description || '',
    popular: item.metadata?.popular === true
  })) || [];

  if (states.length === 0) return null;

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {section.title || 'Popular States'}
          </h2>
          <p className="text-xl text-gray-600">
            {section.subtitle || 'Choose the best state for your business.'}
          </p>
        </motion.div>

        {/* Content field display */}
        {section.content && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto mb-12"
          >
            <div className="prose prose-lg max-w-none text-center">
              <div dangerouslySetInnerHTML={{ __html: section.content }} />
            </div>
          </motion.div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {states.map((state, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className={`bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 ${
                !!state.popular ? 'ring-2 ring-blue-500' : ''
              }`}
            >
              {!!state.popular && (
                <div className="bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">
                  POPULAR
                </div>
              )}
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {state.name}
              </h3>
              <p className="text-gray-600">{state.benefits}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

interface CTASectionProps {
  section: ServicePageSection;
  countryName?: string;
  heroStyle?: React.CSSProperties;
}

const CTASection: React.FC<CTASectionProps> = ({ section, countryName, heroStyle }) => {
  return (
    <section
      className="py-16 text-white"
      style={heroStyle || { background: 'linear-gradient(to right, #3B82F6, #EF4444)' }}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {section.title || `Ready to Start Your Business in ${countryName || 'This Country'}?`}
          </h2>
          <p className="text-xl opacity-80 mb-8 max-w-2xl mx-auto">
            {section.subtitle || 'Get expert guidance and professional support for your company formation journey.'}
          </p>
          {/* Content field display for CTA section */}
          {section.content && (
            <div className="max-w-2xl mx-auto mb-8 text-center">
              <div className="text-lg opacity-80">
                <div dangerouslySetInnerHTML={{ __html: section.content }} />
              </div>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.a
              href={section.cta_link || '/contact'}
              className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-50 transition-colors duration-200 shadow-xl"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {section.cta_text || 'Free Consultation'}
            </motion.a>
            <motion.a
              href="tel:+1234567890"
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors duration-200"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Call Now
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

interface CustomSectionProps {
  section: ServicePageSection;
}

const CustomSection: React.FC<CustomSectionProps> = ({ section }) => {
  if (!section.content && (!section.items || section.items.length === 0)) return null;

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {(section.title || section.subtitle) && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            {section.title && (
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {section.title}
              </h2>
            )}
            {section.subtitle && (
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {section.subtitle}
              </p>
            )}
          </motion.div>
        )}

        {section.content && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="prose prose-lg max-w-none mx-auto"
            dangerouslySetInnerHTML={{ __html: section.content }}
          />
        )}

        {section.items && section.items.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            {section.items.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="bg-gray-50 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300"
              >
                {item.icon && (
                  <div className="text-3xl mb-4">{item.icon}</div>
                )}
                {item.title && (
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                )}
                {item.description && (
                  <p className="text-gray-600">{item.description}</p>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// Additional Section Components

interface ProcessSectionProps {
  section: ServicePageSection;
}

const ProcessSection: React.FC<ProcessSectionProps> = ({ section }) => {
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';

  if (!section.items || section.items.length === 0) return null;

  const processSteps = section.items.map(item => {
    // Parse metadata if it's a string
    let metadata = item.metadata;
    if (typeof metadata === 'string') {
      try {
        metadata = JSON.parse(metadata);
      } catch (e) {
        metadata = {};
      }
    }

    return {
      title: item.title || '',
      description: item.description || '',
      duration: metadata?.duration || null,
      cost: metadata?.cost || null
    };
  });

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {section.title || 'Our Process'}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {section.subtitle || 'Step-by-step process to get your business up and running.'}
          </p>
        </motion.div>

        {/* Content field display */}
        {section.content && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto mb-12"
          >
            <div className="prose prose-lg max-w-none text-center">
              <div dangerouslySetInnerHTML={{ __html: section.content }} />
            </div>
          </motion.div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {processSteps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 text-center"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-blue-600 font-bold text-lg">{index + 1}</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {step.title}
              </h3>
              <p className="text-gray-600 mb-4">{step.description}</p>
              {(step.duration || step.cost) && (
                <div className="mt-4 pt-4 border-t border-gray-200 flex justify-center gap-4">
                  {step.duration && (
                    <div className="text-sm text-blue-600 font-semibold">
                      ⏱️ {step.duration}
                    </div>
                  )}
                  {step.cost && (
                    <div className="text-sm text-green-600 font-semibold">
                      💰 {step.cost}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

interface FAQSectionProps {
  section: ServicePageSection;
}

const FAQSection: React.FC<FAQSectionProps> = ({ section }) => {
  if (!section.items || section.items.length === 0) return null;

  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {section.title || 'Frequently Asked Questions'}
          </h2>
          <p className="text-xl text-gray-600">
            {section.subtitle || 'Get answers to common questions about our services.'}
          </p>
        </motion.div>

        {/* Content field display */}
        {section.content && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto mb-12"
          >
            <div className="prose prose-lg max-w-none text-center">
              <div dangerouslySetInnerHTML={{ __html: section.content }} />
            </div>
          </motion.div>
        )}

        <div className="space-y-6">
          {section.items.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-gray-50 rounded-lg p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {item.title}
              </h3>
              <p className="text-gray-600">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Main Section Mapper Component

interface SectionMapperProps {
  sections: ServicePageSection[];
  countryName?: string;
  heroStyle?: React.CSSProperties;
  countryCode?: string;
  heroColors?: {
    primary: string;
    secondary: string;
  };
}

const SectionMapper: React.FC<SectionMapperProps> = ({ sections, countryName, heroStyle, countryCode, heroColors }) => {
  // Helper function to convert hex colors to Tailwind color names
  const getColorNameFromHex = (hexColor: string): string => {
    const hex = hexColor.toLowerCase();

    // Green shades
    if (hex.includes('#16a34a') || hex.includes('#22c55e') || hex.includes('#15803d') || hex.includes('#166534')) {
      return 'green';
    }
    // Red shades
    if (hex.includes('#ef4444') || hex.includes('#dc2626') || hex.includes('#b91c1c') || hex.includes('#991b1b')) {
      return 'red';
    }
    // Purple shades
    if (hex.includes('#8b5cf6') || hex.includes('#7c3aed') || hex.includes('#6d28d9') || hex.includes('#5b21b6')) {
      return 'purple';
    }
    // Orange shades
    if (hex.includes('#f97316') || hex.includes('#ea580c') || hex.includes('#c2410c') || hex.includes('#9a3412')) {
      return 'orange';
    }
    // Yellow shades
    if (hex.includes('#eab308') || hex.includes('#ca8a04') || hex.includes('#a16207') || hex.includes('#854d0e')) {
      return 'yellow';
    }
    // Default to blue for any other color
    return 'blue';
  };

  const renderSection = (section: ServicePageSection, index: number) => {
    if (!section.is_active) return null;

    const key = `${section.section_type}-${section.id}-${index}`;

    switch (section.section_type) {
      case 'benefits':
        return <BenefitsSection key={key} section={section} countryName={countryName} />;

      case 'sectors':
        return <SectorsSection key={key} section={section} />;

      case 'business_types':
        return <BusinessTypesSection key={key} section={section} />;

      case 'requirements':
        return <RequirementsSection key={key} section={section} />;

      case 'states':
        return <StatesSection key={key} section={section} />;

      case 'process':
        return <ProcessSection key={key} section={section} />;

      case 'faq':
        return <FAQSection key={key} section={section} />;

      case 'cta':
        return <CTASection key={key} section={section} countryName={countryName} heroStyle={heroStyle} />;

      case 'packages':
        return (
          <PackagesSection
            key={key}
            countryCode={countryCode || 'US'}
            countryName={countryName || 'Country'}
            primaryColor={getColorNameFromHex(heroColors?.primary || '#3B82F6')}
            secondaryColor={getColorNameFromHex(heroColors?.secondary || '#EF4444')}
          />
        );

      case 'custom':
        return <CustomSection key={key} section={section} />;

      default:
        // For unknown section types, render as custom section
        return <CustomSection key={key} section={section} />;
    }
  };

  // Sort sections by display_order and render them
  const sortedSections = [...sections].sort((a, b) => a.display_order - b.display_order);

  return (
    <>
      {sortedSections.map((section, index) => renderSection(section, index))}
    </>
  );
};

export default SectionMapper;